import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { arrayMove } from "@dnd-kit/sortable";
import { ToastContainer } from "react-toastify";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import SeriesShow from "./components/SeriesShow";

const SeriesShowContext = createContext();

const POLL_INTERVAL_MS = 1000;
// A series-only cancel leaves every show alone, so shows that still report
// can_cancel do not by themselves mean the worker is mid-run. The set shrinks
// while it is working; once it holds still there is nothing left to wait for.
const UNCHANGED_POLLS_BEFORE_SETTLED = 3;

const cancelableRowKey = (data) =>
  (data.schedule || []).filter((row) => row.can_cancel).map((row) => row.id).join(",");

const awaitsRefresh = (data) =>
  data.state === "canceled"
    ? cancelableRowKey(data) !== ""
    : (data.schedule || []).some((row) => row.pending);

export function useSeriesShowContext() {
  return useContext(SeriesShowContext);
}

export const SeriesShowProvider = (props) => {
  const {
    teamId,
    csrfToken,
    canCancelCalendarEvent,
    canManageSeries,
    seriesDeletionBlockedReason,
    listedAsCollection,
    collectionsPath,
  } = props;

  const [series, setSeries] = useState(props.series);
  const latestSeriesRequest = useRef(0);
  const reorderSavesInFlight = useRef(0);
  const [reorderInFlight, setReorderInFlight] = useState(false);

  const fetchLatestSeries = async () => {
    const requestId = latestSeriesRequest.current + 1;
    latestSeriesRequest.current = requestId;
    const response = await axios.get(`/teams/${teamId}/event_series/${series.id}.json`);
    const superseded = requestId !== latestSeriesRequest.current || reorderSavesInFlight.current > 0;
    return { data: response.data, superseded };
  };

  const applySeries = (data) => {
    latestSeriesRequest.current += 1;
    setSeries(data);
  };

  const shouldPoll = awaitsRefresh(series);

  useEffect(() => {
    if (!shouldPoll) return undefined;
    let timeoutId;
    let cancelled = false;
    let lastCancelableKey = cancelableRowKey(series);
    let unchangedPolls = 0;
    let publishFailureReported = false;
    const poll = async () => {
      try {
        const { data, superseded } = await fetchLatestSeries();
        if (cancelled) return;
        if (series.state === "publishing" && data.state === "draft" && !publishFailureReported) {
          publishFailureReported = true;
          toastError("Publishing didn't finish. Open the series editor and publish again.");
        }
        if (superseded) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
          return;
        }
        setSeries(data);
        if (!awaitsRefresh(data)) return;
        if (data.state === "canceled") {
          const key = cancelableRowKey(data);
          unchangedPolls = key === lastCancelableKey ? unchangedPolls + 1 : 0;
          lastCancelableKey = key;
          if (unchangedPolls >= UNCHANGED_POLLS_BEFORE_SETTLED) return;
        }
        timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        toastError("Could not refresh the series. Please reload the page.");
      }
    };
    timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [shouldPoll, teamId, series.id]);

  const refreshSeries = async () => {
    const { data, superseded } = await fetchLatestSeries();
    if (superseded) return;
    setSeries(data);
  };

  const handleUpdateShowActivity = async (column, value) => {
    const response = await axios.patch(
      `/teams/${teamId}/event_series/${series.id}.json`,
      { event_series: { [column]: value } },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    applySeries(response.data);
  };

  const handleAddShow = async (startTime) => {
    await axios.post(
      `/teams/${teamId}/event_series/${series.id}/event_series_events.json`,
      { event_series_event: { start_time: startTime } },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshSeries();
  };

  // refresh defaults on for single attaches; the batch path passes refresh:false
  // and refreshes once after all POSTs rather than re-fetching the whole
  // dashboard per show.
  const handleAttachConfirm = async (calendarEventId, { refresh = true } = {}) => {
    await axios.post(
      `/teams/${teamId}/event_series/${series.id}/event_series_events.json`,
      { event_series_event: { calendar_event_id: calendarEventId } },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    if (refresh) await refreshSeries();
  };

  const handleAddMemberSeries = async (memberSeriesId, showTypes = null) => {
    await axios.post(
      `/teams/${teamId}/event_series/${series.id}/event_series_memberships.json`,
      { member_series_id: memberSeriesId, ...(showTypes ? { show_types: showTypes } : {}) },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
  };

  // Null covers every show type.
  const handleUpdateMembershipShowTypes = async (membership, showTypes) => {
    await axios.patch(
      `${membership.update_url}.json`,
      { show_types: showTypes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleRemoveMembership = async (membership) => {
    try {
      await axios.delete(membership.remove_url, { headers: { "X-CSRF-Token": csrfToken } });
      await refreshSeries();
    } catch (e) {
      toastError(`Could not remove ${membership.name} from this collection. Please try again.`);
    }
  };

  const handleRemoveRow = async (row) => {
    try {
      await axios.delete(row.remove_url, { headers: { "X-CSRF-Token": csrfToken } });
      await refreshSeries();
    } catch (e) {
      toastError("Could not remove this date from the series. Please try again.");
    }
  };

  const handleCancelRow = async (row) => {
    try {
      await axios.post(row.cancel_url, {}, { headers: { "X-CSRF-Token": csrfToken } });
      await refreshSeries();
    } catch (e) {
      toastError("Could not cancel this event. Please try again.");
    }
  };

  const handleCancelSeries = async (scope, calendarEventIds = []) => {
    try {
      const response = await axios.post(
        `/teams/${teamId}/event_series/${series.id}/cancel.json`,
        { scope, calendar_event_ids: calendarEventIds },
        { headers: { "X-CSRF-Token": csrfToken } },
      );
      applySeries(response.data);
    } catch (e) {
      const reason = e.response?.data?.errors?.[0];
      toastError(reason ? `Could not cancel this series: ${reason}.` : "Could not cancel this series. Please try again.");
    }
  };

  const handleDeleteSeries = async (scope, calendarEventIds = []) => {
    try {
      const response = await axios.delete(
        `/teams/${teamId}/event_series/${series.id}.json`,
        { headers: { "X-CSRF-Token": csrfToken }, data: { scope, calendar_event_ids: calendarEventIds } },
      );
      // 202 means the shows are still being cancelled and the series is only
      // deleted once they all land, so there is nothing to send the operator to
      // yet — the index would still list it.
      if (response.status === 202) {
        toastSuccess("Cancelling this series' shows. It will be deleted once they're done.");
        await refreshSeries();
        return;
      }
      window.location = `/teams/${teamId}/event_series`;
    } catch (e) {
      const reason = e.response?.data?.errors?.[0];
      toastError(reason ? `Could not delete this series: ${reason}.` : "Could not delete this series. Please try again.");
    }
  };

  const handleCreatePassProduct = async (attributes) => {
    const response = await axios.post(
      `/teams/${teamId}/event_series/${series.id}/series_pass_products.json`,
      { series_pass_product: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshSeries();
    return response.data;
  };

  const handleUpdatePassProduct = async (productId, attributes) => {
    await axios.patch(
      `/teams/${teamId}/event_series/${series.id}/series_pass_products/${productId}.json`,
      { series_pass_product: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );

    try {
      await refreshSeries();
    } catch (e) {
      toastError("Saved, but the page couldn't refresh. Reload to see the change.");
    }
  };

  const moveSeriesPassProduct = (productId, toIndex) => {
    setSeries((current) => {
      const fromIndex = current.series_pass_products.findIndex((product) => product.id === productId);
      if (fromIndex === -1) return current;

      return { ...current, series_pass_products: arrayMove(current.series_pass_products, fromIndex, toIndex) };
    });
  };

  const savePassProductPosition = async (productId, position) => {
    reorderSavesInFlight.current += 1;
    try {
      await axios.patch(
        `/teams/${teamId}/event_series/${series.id}/series_pass_products/${productId}.json`,
        { series_pass_product: { position } },
        { headers: { "X-CSRF-Token": csrfToken } },
      );
    } finally {
      reorderSavesInFlight.current -= 1;
    }
  };

  const handleReorderPassProduct = async (productId, overProductId) => {
    const products = series.series_pass_products;
    const oldIndex = products.findIndex((product) => product.id === productId);
    const newIndex = products.findIndex((product) => product.id === overProductId);
    if (oldIndex === -1) return;
    if (newIndex === -1) return;

    moveSeriesPassProduct(productId, newIndex);
    setReorderInFlight(true);

    try {
      await savePassProductPosition(productId, newIndex + 1);
      await refreshAfterSave();
    } catch (e) {
      moveSeriesPassProduct(productId, oldIndex);
      await refreshAfterFailedSave();
      throw e;
    } finally {
      setReorderInFlight(reorderSavesInFlight.current > 0);
    }
  };

  const handleQuotePassProduct = async (attributes) => {
    const response = await axios.post(
      `/teams/${teamId}/event_series/${series.id}/series_pass_products/quote.json`,
      { series_pass_product: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    return response.data;
  };

  const handleDeletePassProduct = async (productId) => {
    await axios.delete(
      `/teams/${teamId}/event_series/${series.id}/series_pass_products/${productId}.json`,
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshSeries();
  };

  const handleStampRedemption = async (productId, params) => {
    const response = await axios.post(
      `/teams/${teamId}/event_series/${series.id}/series_pass_products/${productId}/stamp.json`,
      params,
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    try {
      await refreshSeries();
    } catch (e) {
      toastError("Redemption was set up, but the page could not refresh. Reload to see the latest state.");
    }
    return response.data;
  };

  const refreshAfterFailedSave = () =>
    refreshSeries().catch(() => toastError("Could not refresh the series. Please reload the page."));

  const refreshAfterSave = async () => {
    try {
      await refreshSeries();
    } catch (e) {
      toastError("Saved, but the page couldn't refresh. Reload to see the change.");
    }
  };

  const handleCreateGeneralLedgerCode = async (attributes) => {
    const response = await axios.post(
      `/teams/${teamId}/general_ledger_codes.json`,
      { general_ledger_code: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
    return response.data;
  };

  const handleCreateInventoryPool = async (attributes) => {
    await axios.post(
      `/teams/${teamId}/event_series/${series.id}/series_pass_inventory_pools.json`,
      { series_pass_inventory_pool: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleUpdateInventoryPool = async (poolId, attributes) => {
    await axios.patch(
      `/teams/${teamId}/event_series/${series.id}/series_pass_inventory_pools/${poolId}.json`,
      { series_pass_inventory_pool: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleDeleteInventoryPool = async (poolId) => {
    await axios.delete(
      `/teams/${teamId}/event_series/${series.id}/series_pass_inventory_pools/${poolId}.json`,
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleStartPassCollection = async () => {
    try {
      const { data } = await axios.post(
        `/teams/${teamId}/event_series/${series.id}/pass_collection.json`,
        {},
        { headers: { "X-CSRF-Token": csrfToken } },
      );
      window.location = data.url;
      return true;
    } catch (e) {
      toastError("Could not start a collection for this series. Please try again.");
      return false;
    }
  };

  const handleUpdatePassHolderLimits = async (limits) => {
    const response = await axios.patch(
      `/teams/${teamId}/event_series/${series.id}/pass_holder_limit.json`,
      limits,
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
    return response;
  };

  const handleCreateSurcharge = async (attributes) => {
    await axios.post(
      `/teams/${teamId}/require_per_ticket_custom_charge_types.json`,
      { custom_charge_type: { ...attributes, event_series_id: series.id } },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleUpdateSurcharge = async (surchargeId, attributes) => {
    await axios.patch(
      `/teams/${teamId}/require_per_ticket_custom_charge_types/${surchargeId}.json`,
      { custom_charge_type: attributes },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleDeleteSurcharge = async (surchargeId) => {
    await axios.delete(
      `/teams/${teamId}/require_per_ticket_custom_charge_types/${surchargeId}.json`,
      { headers: { "X-CSRF-Token": csrfToken } },
    );
    await refreshAfterSave();
  };

  const handleIssueComplimentaryPass = async (productId, attributes) => {
    let response;
    try {
      response = await axios.post(
        `/teams/${teamId}/event_series/${series.id}/series_pass_products/${productId}/complimentary_series_passes.json`,
        { complimentary_series_pass: attributes },
        { headers: { "X-CSRF-Token": csrfToken } },
      );
    } catch (e) {
      await refreshAfterFailedSave();
      throw e;
    }
    if (!response.data?.id) {
      await refreshAfterFailedSave();
      return response.data;
    }
    await refreshAfterSave();
    return response.data;
  };

  const handleExportPassHolders = async (productId) => {
    await axios.post(
      "/csv_exports",
      { csv_export: { type: "series_pass_holders", exportable_id: productId } },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
  };

  const value = {
    teamId,
    csrfToken,
    canCancelCalendarEvent,
    canManageSeries,
    seriesDeletionBlockedReason,
    listedAsCollection,
    collectionsPath,
    series,
    reorderInFlight,
    refreshSeries,
    applySeries,
    handleUpdateShowActivity,
    handleAddShow,
    handleAttachConfirm,
    handleAddMemberSeries,
    handleRemoveMembership,
    handleUpdateMembershipShowTypes,
    handleRemoveRow,
    handleCancelRow,
    handleCancelSeries,
    handleDeleteSeries,
    handleCreatePassProduct,
    handleUpdatePassProduct,
    handleReorderPassProduct,
    handleQuotePassProduct,
    handleDeletePassProduct,
    handleStampRedemption,
    handleExportPassHolders,
    handleIssueComplimentaryPass,
    handleCreateGeneralLedgerCode,
    handleCreateInventoryPool,
    handleUpdateInventoryPool,
    handleDeleteInventoryPool,
    handleUpdatePassHolderLimits,
    handleStartPassCollection,
    handleCreateSurcharge,
    handleUpdateSurcharge,
    handleDeleteSurcharge,
  };

  return (
    <SeriesShowContext.Provider value={value}>
      {props.children}
    </SeriesShowContext.Provider>
  );
};

SeriesShowProvider.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  series: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  canCancelCalendarEvent: PropTypes.bool,
  canManageSeries: PropTypes.bool,
  seriesDeletionBlockedReason: PropTypes.string,
  listedAsCollection: PropTypes.bool,
  collectionsPath: PropTypes.string,
  children: PropTypes.node,
};

const SeriesShowApp = ({
  series,
  teamId,
  canCancelCalendarEvent,
  canManageSeries,
  seriesDeletionBlockedReason,
  listedAsCollection,
  collectionsPath,
  csrfToken,
}) => (
  <SeriesShowProvider
    series={series}
    teamId={teamId}
    csrfToken={csrfToken}
    canCancelCalendarEvent={canCancelCalendarEvent}
    canManageSeries={canManageSeries}
    seriesDeletionBlockedReason={seriesDeletionBlockedReason}
    listedAsCollection={listedAsCollection}
    collectionsPath={collectionsPath}
  >
    <SeriesShow />
    <ToastContainer icon={false} />
  </SeriesShowProvider>
);

SeriesShowApp.propTypes = {
  series: PropTypes.object.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  csrfToken: PropTypes.string.isRequired,
  canCancelCalendarEvent: PropTypes.bool,
  canManageSeries: PropTypes.bool,
  seriesDeletionBlockedReason: PropTypes.string,
  listedAsCollection: PropTypes.bool,
  collectionsPath: PropTypes.string,
};

export default SeriesShowApp;
